import { HttpClient } from "@angular/common/http";
import { computed, effect, inject, Injectable, signal } from "@angular/core";
import { Router } from "@angular/router";
import { firstValueFrom } from "rxjs";
import { AuthUser } from "../model/auth-user";
import { Client } from "../model/client";
import { LoginError } from "../model/login-error";
import { RegisterResponse } from "../model/register-response";
import { User } from "../model/user";
import { EnvironmentService } from "./environment.service";

const USER_STORAGE_KEY = '_cln';
const LAST_VISITED_URL_KEY = '_softrans_last_visited_url';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  #userSignal = signal<AuthUser | null>(null);

  user = this.#userSignal.asReadonly();

  isLoggedIn = computed(() => !!this.user());

  private http = inject(HttpClient);
  private router = inject(Router);
  private envService = inject(EnvironmentService);
  private apiUrl: string = this.envService.getValue('apiUrl');

  constructor() {
    this.loadUserFromSession();
    effect(() => {
      const user = this.user();
      if (user) {
        sessionStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
      }
    });
  }

  setLastVisitedUrl(url: string) {
    sessionStorage.setItem(LAST_VISITED_URL_KEY, url);
  }

  getLastVisitedUrl(): string | null {
    return sessionStorage.getItem(LAST_VISITED_URL_KEY);
  }

  loadUserFromSession() {
    const json = sessionStorage.getItem(USER_STORAGE_KEY);
    if (json) {
      const user = JSON.parse(json);
      this.#userSignal.set(user);
    }
  }

  async login(login: string, password: string): Promise<AuthUser | LoginError> {
    const login$ = this.http.post<AuthUser | LoginError>(
      `${this.apiUrl}/clin`, {
      login: login,
      password: password
    });
    const user = await firstValueFrom(login$);

    if ('is_error' in user) {
      throw user.content;
    }

    this.#userSignal.set(user);
    return user;
  }

  setVatRate(vatRate: number) {
    const user = this.#userSignal();
    if (user != null && user.session) {
      user.session = {
        ...user.session,
        vatRate: vatRate,
      };
      this.#userSignal.set(user);
      sessionStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
    }
  }

  async logout() {
    sessionStorage.removeItem(USER_STORAGE_KEY);
    this.#userSignal.set(null);
    await this.router.navigateByUrl('/login');
  }

  async updateClient(client: Partial<Client>): Promise<Client> {
    const client$ = this.http.put<Client>(
      `${this.apiUrl}/clients/${client.id}`,
      client
    );

    const updatedClient = await firstValueFrom(client$);
    let currentUser = {
      ... this.#userSignal(),
      ...updatedClient
    };
    this.#userSignal.set(currentUser);

    return updatedClient;
  }

  async createUser(user: User): Promise<RegisterResponse> {
    const response$ = this.http.post<RegisterResponse>(
      `${this.apiUrl}/register`,
      user
    );

    const response = await firstValueFrom(response$);

    if (response.is_error) {
      throw response.content;
    }

    return response;
  }

  async forgotPassword(email: string): Promise<string> {
    const message$ = this.http.post<string>(
      `${this.apiUrl}/pwreset`,
      {
        email: email,
        source: 'subscriptions'
      }
    );
    return await firstValueFrom(message$);
  }

  async resetPassword(code: string, password: string): Promise<string> {
    const message$ = this.http.post<string>(
      `${this.apiUrl}/pwreset`,
      {
        validation_code: code,
        password: password
      }
    );
    return await firstValueFrom(message$);
  }

  async verifyEmail(sid: string, cid: string, code: string): Promise<string> {
    const message$ = this.http.post<string>(
      `${this.apiUrl}/validate`,
      {
        s: sid,
        n: cid,
        c: code,
      }
    );

    return await firstValueFrom(message$);
  }

}
